import React from 'react';
import { CheckCircle } from 'lucide-react';

const AccreditationsSection = () => {
  const accreditations = [
    {
      id: "netcc",
      title: "NETCC Approved Seller",
      shortTitle: "NETCC Approved",
      description: "We're authorised under the New Energy Tech Consumer Code, an ACCC-approved program ensuring the highest consumer protection standards.",
      benefits: [
        "Ethical sales with no pressure tactics",
        "Clear, transparent quotes with no hidden costs",
        "Quality installation standards",
        "Complaints handled promptly and fairly"
      ],
      logo: "/netcc-approved-seller.png",
      color: "#0B4A6F"
    },
    {
      id: "solar-vic",
      title: "Solar Victoria Approved Retailer",
      shortTitle: "Solar Victoria Approved",
      description: "Authorised to deliver Solar Victoria rebate programs, ensuring you can access government incentives with confidence.",
      benefits: [
        "Access to Solar Homes Program rebates",
        "Interest-free loan eligibility",
        "Compliance with Victorian standards",
        "Quality-assured installations"
      ],
      logo: null,
      color: "#1E40AF"
    },
    {
      id: "cec",
      title: "Accredited Installers",
      shortTitle: "Accredited Installers",
      description: "All our installations are performed by accredited professionals who meet rigorous training and safety standards.",
      benefits: [
        "Fully qualified technicians",
        "Compliant with Australian Standards",
        "Eligible for STC rebates",
        "Professional workmanship guarantee"
      ],
      logo: null,
      color: "#059669"
    }
  ];

  return (
    <section className="py-12 md:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-4">
            Trust & Compliance
          </span>
          <h2 className="heading-2 mb-5">
            Your Protection is Our Priority
          </h2>
          <p className="body-large">
            We maintain the highest industry accreditations to ensure quality, safety, 
            and consumer protection. When you choose Supa Energy Hub, you're protected 
            by multiple layers of safeguards.
          </p>
        </div>

        {/* NETCC Highlight Banner */}
        <div className="bg-gradient-to-r from-[#0B4A6F] to-[#0D5A84] rounded-2xl p-6 md:p-8 mb-8 text-white">
          <div className="flex flex-col md:flex-row items-center gap-6">
            {/* NETCC Logo */}
            <div className="flex-shrink-0 bg-white rounded-xl p-4 flex items-center justify-center">
              <img 
                src="/netcc-approved-seller.png" 
                alt="NETCC Approved Seller" 
                className="h-16 w-auto"
              />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-bold mb-2">New Energy Tech Consumer Code Approved Seller</h3>
              <p className="text-white/80 text-sm leading-relaxed">
                We're one of over 2,000 Australian retailers committed to the ACCC-authorised consumer protection 
                code. This means ethical sales practices, transparent pricing, quality installations, and reliable 
                after-sales support — all backed by independent oversight.
              </p>
            </div>
            <div className="flex-shrink-0">
              <a 
                href="https://www.newenergytech.org.au/find-an-approved-seller" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
              >
                Verify on NETCC
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Accreditations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {accreditations.map((acc) => (
            <div 
              key={acc.id}
              className="bg-white rounded-2xl border border-black/5 p-6 hover:shadow-lg transition-shadow"
            >
              {/* Icon/Logo */}
              <div className="flex items-center gap-3 mb-4">
                {acc.logo ? (
                  <div className="w-12 h-12 rounded-xl bg-[#0B4A6F]/10 flex items-center justify-center">
                    <img src={acc.logo} alt={acc.title} className="w-8 h-8" />
                  </div>
                ) : (
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${acc.color}15` }}
                  >
                    <svg className="w-6 h-6" style={{ color: acc.color }} fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                    </svg>
                  </div>
                )}
                <h3 className="font-semibold text-[#3D5A47]">{acc.shortTitle}</h3>
              </div>
              
              {/* Description */}
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                {acc.description}
              </p>
              
              {/* Benefits */}
              <ul className="space-y-2">
                {acc.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-[#B5D43B] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Trust Statement */}
        <div className="mt-10 text-center">
          <div className="inline-flex flex-wrap items-center justify-center gap-6 px-8 py-4 bg-[#3D5A47]/5 rounded-full">
            <div className="flex items-center gap-2">
              <img 
                src="/netcc-approved-seller.png" 
                alt="NETCC" 
                className="h-6 w-auto"
              />
            </div>
            <div className="w-px h-6 bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6 text-[#1E40AF]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
              <span className="text-sm font-medium text-[#3D5A47]">Solar Victoria Approved</span>
            </div>
            <div className="w-px h-6 bg-gray-300 hidden sm:block"></div>
            <div className="flex items-center gap-2">
              <svg className="w-6 h-6 text-[#059669]" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
              <span className="text-sm font-medium text-[#3D5A47]">Accredited Installers</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AccreditationsSection;
